import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
} from "react";
import axios from "axios";
import { API_BASE_URL } from "../constants";
import { useClub } from "./ClubContext";
import { useAuth } from "./AuthContext";
import {
  FormationTag,
  SchemeFormation,
  OffensivePlayTag,
  OffensivePlayConcept,
  OffensiveRunConcept,
  OffensivePassConcept,
  OffensivePassProtectionConcept,
  OffensiveSituation,
  OffensiveGamePlan,
  OffensivePlaybookPlay,
  Front,
  Stunt,
  Coverage,
  KickOffReturnPlay,
  FieldGoalPlay,
  PuntPlay,
  PuntReturnPlay,
  FieldGoalBlockPlay,
  KickOffPlay,
} from "../types/SchemesTypes";

import { Game } from "../types/ScheduleTypes";
import { PersonnelGroup, Side } from "../types/DepthChartTypes";

// Define the shape of the context
interface SchemesContextType {
  // Data states
  isLoading: boolean;
  error: string | null;
  // all
  personnel: PersonnelGroup[];

  // offense
  formations: SchemeFormation[];
  formationTags: FormationTag[];
  offensivePlayTags: OffensivePlayTag[];
  playConcepts: OffensivePlayConcept[];
  runConcepts: OffensiveRunConcept[];
  passConcepts: OffensivePassConcept[];
  passProtectionConcepts: OffensivePassProtectionConcept[];
  offensivePersonnel: PersonnelGroup[];
  offensivePlaybookPlays: OffensivePlaybookPlay[];
  offensiveSituations: OffensiveSituation[];
  // deprecated (perhaps reinstate in future versions)
  offensiveGamePlans: OffensiveGamePlan[];

  // defense
  defensivePersonnel: PersonnelGroup[];
  fronts: Front[];
  stunts: Stunt[];
  coverages: Coverage[];
  // kickoff
  kickOffPersonnel: PersonnelGroup[];
  kickOffPlays: KickOffPlay[];
  // kickoff return
  kickOffReturnPersonnel: PersonnelGroup[];
  kickOffReturnPlays: KickOffReturnPlay[];
  // punt
  puntPersonnel: PersonnelGroup[];
  puntPlays: PuntPlay[];
  // punt return
  puntReturnPersonnel: PersonnelGroup[];
  puntReturnPlays: PuntReturnPlay[];
  // field goal
  fieldGoalPersonnel: PersonnelGroup[];
  fieldGoalPlays: FieldGoalPlay[];
  // field goal block
  fieldGoalBlockPersonnel: PersonnelGroup[];
  fieldGoalBlockPlays: FieldGoalBlockPlay[];

  // Data fetching methods
  // all
  fetchPersonnel: () => Promise<void>;
  // offense
  fetchFormations: () => Promise<void>;
  fetchTags: () => Promise<void>;
  fetchFormationTags: () => Promise<void>;
  fetchPlayTags: () => Promise<void>;
  fetchPlayConcepts: () => Promise<void>;
  fetchRunConcepts: () => Promise<void>;
  fetchPassConcepts: () => Promise<void>;
  fetchPassProtectionConcepts: () => Promise<void>;
  fetchOffensiveSituations: () => Promise<void>;
  // deprecated (perhaps reinstate in future versions)
  fetchOffensiveGamePlans: () => Promise<void>;
  // defense
  fetchFronts: () => Promise<void>;
  fetchStunts: () => Promise<void>;
  fetchCoverages: () => Promise<void>;
  // special teams
  fetchKickOffPlays: () => Promise<void>;
  fetchKickOffReturnPlays: () => Promise<void>;
  fetchPuntPlays: () => Promise<void>;
  fetchPuntReturnPlays: () => Promise<void>;
  fetchFieldGoalPlays: () => Promise<void>;
  fetchFieldGoalBlockPlays: () => Promise<void>;

  // Data mutation methods
  // all
  savePersonnelGroup: (personnelGroup: {
    id?: number;
    name: string;
    side: Side;
    description?: string;
  }) => Promise<any>;
  deletePersonnelGroup: (id: number) => Promise<boolean>;

  // offense

  saveFormation: (formation: SchemeFormation) => Promise<any>;
  deleteFormation: (id: number) => Promise<boolean>;
  saveFormationTag: (formationTag: {
    id?: number;
    name: string;
    description: string | null;
  }) => Promise<any>;
  deleteFormationTag: (id: number) => Promise<boolean>;
  savePlayTag: (playTag: {
    id?: number;
    name: string;
    description: string | null;
  }) => Promise<any>;
  deletePlayTag: (id: number) => Promise<boolean>;
  savePlayConcept: (concept: OffensivePlayConcept) => Promise<any>;
  deletePlayConcept: (id: number) => Promise<boolean>;
  saveRunConcept: (concept: OffensiveRunConcept) => Promise<any>;
  deleteRunConcept: (id: number) => Promise<boolean>;
  savePassConcept: (concept: OffensivePassConcept) => Promise<any>;
  deletePassConcept: (id: number) => Promise<boolean>;
  savePassProtectionConcept: (
    concept: OffensivePassProtectionConcept
  ) => Promise<any>;
  deletePassProtectionConcept: (id: number) => Promise<boolean>;
  // deprecated (perhaps reinstate in future versions)
  generateOffensivePlaybookPlays: () => void;
  generateGameplanPlays: (
    plays: OffensivePlaybookPlay[]
  ) => OffensivePlaybookPlay[];
  saveOffensiveGamePlan: (gameplan: {
    name: string;
    description: string;
    game: Game;
    plays: OffensivePlaybookPlay[];
  }) => Promise<any>;
  updateOffensiveGamePlan: (gamePlan: OffensiveGamePlan) => Promise<any>;
  deleteOffensiveGamePlan: (id: number) => Promise<boolean>;
  // defense
  saveFront: (front: {
    id?: number;
    name: string;
    description: string;
    club: number;
  }) => Promise<any>;
  deleteFront: (id: number) => Promise<boolean>;
  saveStunt: (stunt: {
    id?: number;
    name: string;
    description: string;
    club: number;
  }) => Promise<any>;
  deleteStunt: (id: number) => Promise<boolean>;
  saveCoverage: (coverage: {
    id?: number;
    name: string;
    description: string;
    club: number;
  }) => Promise<any>;
  deleteCoverage: (id: number) => Promise<boolean>;
  // kickoff
  saveKickOffPlay: (play: KickOffPlay) => Promise<any>;
  deleteKickOffPlay: (id: number) => Promise<boolean>;
  // kickoff return
  saveKickOffReturnPlay: (play: KickOffReturnPlay) => Promise<any>;
  deleteKickOffReturnPlay: (id: number) => Promise<boolean>;
  // punt
  savePuntPlay: (play: PuntPlay) => Promise<any>;
  deletePuntPlay: (id: number) => Promise<boolean>;
  // punt return
  savePuntReturnPlay: (play: PuntReturnPlay) => Promise<any>;
  deletePuntReturnPlay: (id: number) => Promise<boolean>;
  // field goal
  saveFieldGoalPlay: (play: FieldGoalPlay) => Promise<any>;
  deleteFieldGoalPlay: (id: number) => Promise<boolean>;
  // field goal block
  saveFieldGoalBlockPlay: (play: FieldGoalBlockPlay) => Promise<any>;
  deleteFieldGoalBlockPlay: (id: number) => Promise<boolean>;
}

// Create the context with a default value
const SchemesContext = createContext<SchemesContextType | undefined>(undefined);

// Provider component
export const SchemesProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { club } = useClub();
  const { isAuthenticated } = useAuth();

  // Data states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // all
  const [personnel, setPersonnel] = useState<PersonnelGroup[]>([]);
  // offense
  const [formations, setFormations] = useState<SchemeFormation[]>([]);
  const [formationTags, setFormationTags] = useState<FormationTag[]>([]);
  const [offensivePlayTags, setOffensivePlayTags] = useState<
    OffensivePlayTag[]
  >([]);
  const [playConcepts, setPlayConcepts] = useState<OffensivePlayConcept[]>([]);
  const [runConcepts, setRunConcepts] = useState<OffensiveRunConcept[]>([]);
  const [passConcepts, setPassConcepts] = useState<OffensivePassConcept[]>([]);
  const [passProtectionConcepts, setPassProtectionConcepts] = useState<
    OffensivePassProtectionConcept[]
  >([]);
  const [offensivePersonnel, setOffensivePersonnel] = useState<
    PersonnelGroup[]
  >([]);
  const [offensivePlaybookPlays, setOffensivePlaybookPlays] = useState<
    OffensivePlaybookPlay[]
  >([]);
  const [offensiveSituations, setOffensiveSituations] = useState<
    OffensiveSituation[]
  >([]);
  // deprecated (perhaps reinstate in future versions)
  const [offensiveGamePlans, setOffensiveGamePlans] = useState<
    OffensiveGamePlan[]
  >([]);

  // defense

  const [defensivePersonnel, setDefensivePersonnel] = useState<
    PersonnelGroup[]
  >([]);
  const [fronts, setFronts] = useState<Front[]>([]);
  const [stunts, setStunts] = useState<Stunt[]>([]);
  const [coverages, setCoverages] = useState<Coverage[]>([]);
  // kickoff
  const [kickOffPersonnel, setKickOffPersonnel] = useState<PersonnelGroup[]>(
    []
  );
  const [kickOffPlays, setKickOffPlays] = useState<KickOffPlay[]>([]);

  // kickoff return
  const [kickOffReturnPersonnel, setKickOffReturnPersonnel] = useState<
    PersonnelGroup[]
  >([]);
  const [kickOffReturnPlays, setKickOffReturnPlays] = useState<
    KickOffReturnPlay[]
  >([]);

  // punt
  const [puntPersonnel, setPuntPersonnel] = useState<PersonnelGroup[]>([]);
  const [puntPlays, setPuntPlays] = useState<PuntPlay[]>([]);
  // punt return
  const [puntReturnPersonnel, setPuntReturnPersonnel] = useState<
    PersonnelGroup[]
  >([]);
  const [puntReturnPlays, setPuntReturnPlays] = useState<PuntReturnPlay[]>([]);

  // field goal
  const [fieldGoalPersonnel, setFieldGoalPersonnel] = useState<
    PersonnelGroup[]
  >([]);
  const [fieldGoalPlays, setFieldGoalPlays] = useState<FieldGoalPlay[]>([]);

  // field goal block
  const [fieldGoalBlockPersonnel, setFieldGoalBlockPersonnel] = useState<
    PersonnelGroup[]
  >([]);
  const [fieldGoalBlockPlays, setFieldGoalBlockPlays] = useState<
    FieldGoalBlockPlay[]
  >([]);

  // Fetch data only when authenticated and club is available
  useEffect(() => {
    if (isAuthenticated && club) {
      fetchFormations();
      fetchTags();
      fetchFormationTags();
      fetchPlayTags();
      fetchPlayConcepts();
      fetchRunConcepts();
      fetchPassConcepts();
      fetchPassProtectionConcepts();
      fetchPersonnel();
      fetchOffensiveSituations();
      fetchKickOffPlays();
      fetchKickOffReturnPlays();
      fetchPuntPlays();
      fetchPuntReturnPlays();
      fetchFieldGoalPlays();
      fetchFieldGoalBlockPlays();
      fetchFronts();
      fetchStunts();
      fetchCoverages();
    }
  }, [isAuthenticated, club]);

  // Generate offensive playbook plays
  useEffect(() => {
    if (runConcepts.length > 0 || passConcepts.length > 0) {
      generateOffensivePlaybookPlays();
    }
  }, [runConcepts, passConcepts]);

  // fetch methods
  // all
  const fetchPersonnel = async () => {
    setIsLoading(true);
    await axios
      .get(`${API_BASE_URL}/personnel-groups/`)
      .then((response) => {
        setPersonnel(response.data);
        setOffensivePersonnel(
          response.data.filter(
            (group: PersonnelGroup) => group.side === "OFFENSE"
          )
        );
        setDefensivePersonnel(
          response.data.filter(
            (group: PersonnelGroup) => group.side === "DEFENSE"
          )
        );
        setKickOffReturnPersonnel(
          response.data.filter(
            (group: PersonnelGroup) => group.side === "KICKOFF_RETURN"
          )
        );
        setKickOffPersonnel(
          response.data.filter(
            (group: PersonnelGroup) => group.side === "KICKOFF"
          )
        );
        setPuntPersonnel(
          response.data.filter((group: PersonnelGroup) => group.side === "PUNT")
        );
        setPuntReturnPersonnel(
          response.data.filter(
            (group: PersonnelGroup) => group.side === "PUNT_RETURN"
          )
        );
        setFieldGoalPersonnel(
          response.data.filter(
            (group: PersonnelGroup) => group.side === "FIELD_GOAL"
          )
        );
        setFieldGoalBlockPersonnel(
          response.data.filter(
            (group: PersonnelGroup) => group.side === "FIELD_GOAL_BLOCK"
          )
        );
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching personnel groups:", err);
        setError(err.message || "Error fetching personnel groups");
        setIsLoading(false);
      });
  };

  // offense
  const fetchFormations = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/formations/`);
      setFormations(response.data);
      setIsLoading(false);
    } catch (err: any) {
      console.error("Error fetching formations:", err);
      setError(err.message || "Error fetching formations");
      setIsLoading(false);
    }
  };

  const fetchTags = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/play-tags/`);
      setOffensivePlayTags(response.data);
      setIsLoading(false);
    } catch (err: any) {
      console.error("Error fetching offensive play tags:", err);
      setError(err.message || "Error fetching tags");
      setIsLoading(false);
    }
  };

  const fetchFormationTags = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/formation-tags/`);
      setFormationTags(response.data);
      setIsLoading(false);
    } catch (err: any) {
      console.error("Error fetching formation tags:", err);
      setError(err.message || "Error fetching formation tags");
      setIsLoading(false);
    }
  };

  const fetchPlayTags = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/play-tags/`);
      setOffensivePlayTags(response.data);
      setIsLoading(false);
    } catch (err: any) {
      console.error("Error fetching play tags:", err);
      setError(err.message || "Error fetching play tags");
      setIsLoading(false);
    }
  };

  const fetchPlayConcepts = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/offensive-play-concepts/`
      );
      setPlayConcepts(response.data);
      setIsLoading(false);
    } catch (err: any) {
      console.error("Error fetching play concepts:", err);
      setError(err.message || "Error fetching play concepts");
      setIsLoading(false);
    }
  };

  const fetchRunConcepts = async () => {
    setIsLoading(true);
    await axios
      .get(`${API_BASE_URL}/offensive-run-concepts/`)
      .then((response) => {
        setRunConcepts(response.data);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching run concepts:", err);
        setError(err.message || "Error fetching run concepts");
        setIsLoading(false);
      });
  };

  const fetchPassConcepts = async () => {
    setIsLoading(true);
    await axios
      .get(`${API_BASE_URL}/offensive-pass-concepts/`)
      .then((response) => {
        setPassConcepts(response.data);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching pass concepts:", err);
        setError(err.message || "Error fetching pass concepts");
        setIsLoading(false);
      });
  };

  const fetchPassProtectionConcepts = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/offensive-pass-protection-concepts/`
      );
      setPassProtectionConcepts(response.data);
      setIsLoading(false);
    } catch (err: any) {
      console.error("Error fetching pass protection concepts:", err);
      setError(err.message || "Error fetching pass protection concepts");
      setIsLoading(false);
    }
  };

  const fetchOffensiveSituations = async () => {
    await axios
      .get(`${API_BASE_URL}/offensive-situations/`)
      .then((response) => {
        setOffensiveSituations(response.data);
      })
      .catch((err) => {
        console.error("Error fetching offensive situations:", err);
      });
  };

  // deprecated (perhaps reinstate in future versions)
  const fetchOffensiveGamePlans = async () => {
    setIsLoading(true);
    setError(null);
    await axios
      .get(`${API_BASE_URL}/offensive-game-plans/`)
      .then((response) => {
        setOffensiveGamePlans(response.data);
      })
      .catch((err) => {
        console.error("Error fetching offensive game plans:", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // defense
  const fetchFronts = async () => {
    await axios
      .get(`${API_BASE_URL}/fronts/`)
      .then((response) => {
        setFronts(response.data);
      })
      .catch((err) => {
        console.error("Error fetching fronts:", err);
      });
  };

  const fetchStunts = async () => {
    await axios
      .get(`${API_BASE_URL}/stunts/`)
      .then((response) => {
        setStunts(response.data);
      })
      .catch((err) => {
        console.error("Error fetching stunts:", err);
      });
  };

  const fetchCoverages = async () => {
    await axios
      .get(`${API_BASE_URL}/coverages/`)
      .then((response) => {
        setCoverages(response.data);
      })
      .catch((err) => {
        console.error("Error fetching coverages:", err);
      });
  };

  // kickoff
  const fetchKickOffPlays = async () => {
    await axios
      .get(`${API_BASE_URL}/kickoff-plays/`)
      .then((response) => {
        setKickOffPlays(response.data);
      })
      .catch((err) => {
        console.error("Error fetching kickoff plays:", err);
      });
  };
  // kickoff return
  const fetchKickOffReturnPlays = async () => {
    await axios
      .get(`${API_BASE_URL}/kickoff-return-plays/`)
      .then((response) => {
        setKickOffReturnPlays(response.data);
      })
      .catch((err) => {
        console.error("Error fetching kickoff return plays:", err);
      });
  };
  // punt
  const fetchPuntPlays = async () => {
    await axios
      .get(`${API_BASE_URL}/punt-plays/`)
      .then((response) => {
        setPuntPlays(response.data);
      })
      .catch((err) => {
        console.error("Error fetching punt plays:", err);
      });
  };
  // punt return
  const fetchPuntReturnPlays = async () => {
    await axios
      .get(`${API_BASE_URL}/punt-return-plays/`)
      .then((response) => {
        setPuntReturnPlays(response.data);
      })
      .catch((err) => {
        console.error("Error fetching punt return plays:", err);
      });
  };
  // field goal
  const fetchFieldGoalPlays = async () => {
    await axios
      .get(`${API_BASE_URL}/field-goal-plays/`)
      .then((response) => {
        setFieldGoalPlays(response.data);
      })
      .catch((err) => {
        console.error("Error fetching field goal plays:", err);
      });
  };
  // field goal block
  const fetchFieldGoalBlockPlays = async () => {
    await axios
      .get(`${API_BASE_URL}/field-goal-block-plays/`)
      .then((response) => {
        setFieldGoalBlockPlays(response.data);
      })
      .catch((err) => {
        console.error("Error fetching field goal block plays:", err);
      });
  };

  // Data mutation methods
  // all
  const savePersonnelGroup = async (personnelGroup: {
    id?: number;
    name: string;
    side: Side;
    description?: string;
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = {
        ...personnelGroup,
        club: club?.id,
      };

      let response;
      if (personnelGroup.id) {
        response = await axios.put(
          `${API_BASE_URL}/personnel-groups/${personnelGroup.id}/`,
          data
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/personnel-groups/`, data);
      }

      setIsLoading(false);
      await fetchPersonnel();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error saving personnel group"
      );
      console.error("Error saving personnel group:", err);
      throw err;
    }
  };

  const deletePersonnelGroup = async (id: number) => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/personnel-groups/${id}/`);
      setIsLoading(false);
      await fetchPersonnel();
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error deleting personnel group"
      );
      console.error("Error deleting personnel group:", err);
      return false;
    }
  };
  // offense
  const saveFormation = async (formation: SchemeFormation) => {
    setIsLoading(true);
    setError(null);
    await axios
      .put(`${API_BASE_URL}/formations/save_formation/`, formation)
      .then((response) => {
        setIsLoading(false);
        fetchFormations();
      })
      .catch((err: any) => {
        setIsLoading(false);
        console.error("Error saving formation:", err);
        setError(err.message || "Error saving formation");
        setIsLoading(false);
      });
  };

  const deleteFormation = async (id: number) => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/formations/${id}/`);
      setIsLoading(false);
      await fetchFormations();
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message || err.message || "Error deleting formation"
      );
      console.error("Error deleting formation:", err);
      return false;
    }
  };

  const saveFormationTag = async (formationTag: {
    id?: number;
    name: string;
    description: string | null;
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = {
        ...formationTag,
        club: club?.id,
      };

      let response;
      if (formationTag.id) {
        response = await axios.put(
          `${API_BASE_URL}/formation-tags/${formationTag.id}/`,
          data
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/formation-tags/`, data);
      }

      setIsLoading(false);
      await fetchFormationTags();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error saving formation tag"
      );
      console.error("Error saving formation tag:", err);
      throw err;
    }
  };

  const deleteFormationTag = async (id: number) => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/formation-tags/${id}/`);
      setIsLoading(false);
      await fetchFormationTags();
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error deleting formation tag"
      );
      console.error("Error deleting formation tag:", err);
      return false;
    }
  };

  const savePlayTag = async (playTag: {
    id?: number;
    name: string;
    description: string | null;
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = {
        ...playTag,
        club: club?.id,
      };

      let response;
      if (playTag.id) {
        response = await axios.put(
          `${API_BASE_URL}/play-tags/${playTag.id}/`,
          data
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/play-tags/`, data);
      }

      setIsLoading(false);
      await fetchPlayTags();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message || err.message || "Error saving play tag"
      );
      console.error("Error saving play tag:", err);
      throw err;
    }
  };

  const deletePlayTag = async (id: number) => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/play-tags/${id}/`);
      setIsLoading(false);
      await fetchPlayTags();
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message || err.message || "Error deleting play tag"
      );
      console.error("Error deleting play tag:", err);
      return false;
    }
  };

  const savePlayConcept = async (concept: OffensivePlayConcept) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = {
        ...concept,
        club: concept.club || club?.id,
      };

      let response;
      if (concept.id) {
        response = await axios.put(
          `${API_BASE_URL}/offensive-play-concepts/${concept.id}/`,
          data
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/offensive-play-concepts/`,
          data
        );
      }

      setIsLoading(false);
      await fetchPlayConcepts();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error saving play concept"
      );
      console.error("Error saving play concept:", err);
      throw err;
    }
  };

  const deletePlayConcept = async (id: number) => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/offensive-play-concepts/${id}/`);
      setIsLoading(false);
      await fetchPlayConcepts();
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error deleting play concept"
      );
      console.error("Error deleting play concept:", err);
      return false;
    }
  };

  const saveRunConcept = async (concept: OffensiveRunConcept) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = {
        ...concept,
        club: concept.club.id || club?.id,
      };

      let response;
      if (concept.id) {
        response = await axios.patch(
          `${API_BASE_URL}/offensive-run-concepts/${concept.id}/update_run_concept/`,
          data
        );
      } else {
        response = await axios.put(
          `${API_BASE_URL}/offensive-run-concepts/save_run_concept/`,
          data
        );
      }

      setIsLoading(false);
      await fetchRunConcepts();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message || err.message || "Error saving run concept"
      );
      console.error("Error saving run concept:", err);
      throw err;
    }
  };

  const deleteRunConcept = async (id: number) => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/offensive-run-concepts/${id}/`);
      setIsLoading(false);
      await fetchRunConcepts();
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error deleting run concept"
      );
      console.error("Error deleting run concept:", err);
      return false;
    }
  };

  const savePassConcept = async (concept: OffensivePassConcept) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = {
        ...concept,
        club: concept.club || club?.id,
      };

      let response;
      if (concept.id) {
        response = await axios.put(
          `${API_BASE_URL}/offensive-pass-concepts/${concept.id}/`,
          data
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/offensive-pass-concepts/`,
          data
        );
      }

      setIsLoading(false);
      await fetchPassConcepts();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error saving pass concept"
      );
      console.error("Error saving pass concept:", err);
      throw err;
    }
  };

  const deletePassConcept = async (id: number) => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/offensive-pass-concepts/${id}/`);
      setIsLoading(false);
      await fetchPassConcepts();
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error deleting pass concept"
      );
      console.error("Error deleting pass concept:", err);
      return false;
    }
  };

  const savePassProtectionConcept = async (
    concept: OffensivePassProtectionConcept
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = {
        ...concept,
        club: concept.club || club?.id,
      };

      let response;
      if (concept.id) {
        response = await axios.put(
          `${API_BASE_URL}/offensive-pass-protection-concepts/${concept.id}/`,
          data
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/offensive-pass-protection-concepts/`,
          data
        );
      }

      setIsLoading(false);
      await fetchPassProtectionConcepts();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error saving pass protection concept"
      );
      console.error("Error saving pass protection concept:", err);
      throw err;
    }
  };

  const deletePassProtectionConcept = async (id: number) => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(
        `${API_BASE_URL}/offensive-pass-protection-concepts/${id}/`
      );
      setIsLoading(false);
      await fetchPassProtectionConcepts();
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error deleting pass protection concept"
      );
      console.error("Error deleting pass protection concept:", err);
      return false;
    }
  };

  // deprecated (perhaps reinstate in future versions)
  const generateOffensivePlaybookPlays = () => {
    const generatedPlays: OffensivePlaybookPlay[] = [];

    // Process run concepts
    runConcepts.forEach((runConcept, index) => {
      runConcept.personnel_formations.forEach((personnelFormation, fIndex) => {
        const newPlay: OffensivePlaybookPlay = {
          called_pass: false,
          name: runConcept.name,
          code_word_left: runConcept.code_word_left,
          code_word_right: runConcept.code_word_right,
          code_word:
            `${runConcept.code_word_left} / ${runConcept.code_word_right}`.trim(),
          call: `${runConcept.call_left} / ${runConcept.call_right}`.trim(),
          // Keep the string format for backward compatibility with the DataGrid
          personnel_formation: `${personnelFormation.formation.name} (${personnelFormation.personnel_group.name})`,
          // Also provide the separated objects for new functionality
          personnel_group: {
            id: personnelFormation.personnel_group.id,
            name: personnelFormation.personnel_group.name,
          },
          formation: {
            id: personnelFormation.formation.id,
            name: personnelFormation.formation.name,
          },
          concept: runConcept.play_concept,
          run_concept: runConcept,
          tags: runConcept.play_tags,
          offensive_situations: runConcept.offensive_situations,
          strongside: runConcept.strongside,
        };

        generatedPlays.push(newPlay);
      });
    });

    passConcepts.forEach((passConcept, index) => {
      passConcept.personnel_formations?.forEach(
        (personnelFormation, fIndex) => {
          const newPlay: OffensivePlaybookPlay = {
            called_pass: true,
            name: passConcept.name,
            code_word: `${passConcept.name}`,
            code_word_left: passConcept.name, // For pass concepts, use the same name for both
            code_word_right: passConcept.name, // For pass concepts, use the same name for both
            call: `${passConcept.name}`,
            // Keep the string format for backward compatibility with the DataGrid
            personnel_formation: `${personnelFormation.formation.name} (${personnelFormation.personnel_group.name})`,
            // Also provide the separated objects for new functionality
            personnel_group: {
              id: personnelFormation.personnel_group.id,
              name: personnelFormation.personnel_group.name,
            },
            formation: {
              id: personnelFormation.formation.id,
              name: personnelFormation.formation.name,
            },
            concept: passConcept.play_concept || {
              id: 0,
              name: "",
              description: "",
              club: club,
            },
            pass_concept: passConcept,
            offensive_situations: passConcept.offensive_situations || [],
            pass_protection_concepts:
              passConcept.pass_protection_concepts || [],
          };
          generatedPlays.push(newPlay);
        }
      );
    });

    // Update the state with generated plays
    setOffensivePlaybookPlays(generatedPlays);
  };

  const saveOffensiveGamePlan = async (gameplan: {
    name: string;
    description: string;
    game: Game;
    plays: OffensivePlaybookPlay[];
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      // Prepare the data for the API
      const gameplanData = {
        name: gameplan.name,
        description: gameplan.description,
        club: club?.id,
        game: gameplan.game.id,
        offensive_plays: gameplan.plays,
      };

      // Send to the backend
      const response = await axios.post(
        `${API_BASE_URL}/offensive-game-plans/`,
        gameplanData
      );

      // Refresh the game plans list
      await fetchOffensiveGamePlans();

      setIsLoading(false);
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error saving offensive game plan"
      );
      console.error("Error saving offensive game plan:", err);
      throw err;
    }
  };

  const updateOffensiveGamePlan = async (gamePlan: OffensiveGamePlan) => {
    setIsLoading(true);
    setError(null);
    try {
      // Update the game plan via API
      const response = await axios.put(
        `${API_BASE_URL}/offensive-game-plans/${gamePlan.id}/`,
        gamePlan
      );

      // Refresh the list of game plans
      await fetchOffensiveGamePlans();

      setIsLoading(false);
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error updating offensive game plan"
      );
      console.error("Error updating offensive game plan:", err);
      throw err;
    }
  };

  const deleteOffensiveGamePlan = async (id: number): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/offensive-game-plans/${id}/`);
      await fetchOffensiveGamePlans();
      setIsLoading(false);
      return true;
    } catch (err: any) {
      console.error("Error deleting offensive game plan:", err);
      setIsLoading(false);
      return false;
    }
  };

  const generateGameplanPlays = (
    plays: OffensivePlaybookPlay[]
  ): OffensivePlaybookPlay[] => {
    const generatedPlays: OffensivePlaybookPlay[] = [];

    // Process each play
    plays.forEach((play) => {
      // Create variations for each direction
      const directions = ["Left", "Right"];

      // For each direction, create a play
      directions.forEach((direction) => {
        // Determine code words to use based on direction and strongside
        let codeWords: string[] = [];

        // Only apply strongside logic for runs
        if (!play.called_pass) {
          if (play.strongside === undefined) {
            // Use the generic code_word if strongside is undefined
            codeWords = [play.code_word || ""];
          } else if (play.strongside === null) {
            // For strongside === null (both directions), use both code words
            if (play.code_word_left) codeWords.push(play.code_word_left);
            if (play.code_word_right) codeWords.push(play.code_word_right);
            // If neither is defined, just use the generic code_word
            if (codeWords.length === 0) codeWords = [play.code_word || ""];
          } else {
            // For strongside === true or false, determine the appropriate code word
            const isStrongSide = play.strongside;
            const isRightDirection = direction === "Right";

            // If (strong + right) or (!strong + left) use right code word
            // Otherwise use left code word
            if (
              (isStrongSide && isRightDirection) ||
              (!isStrongSide && !isRightDirection)
            ) {
              codeWords = [play.code_word_right || ""];
            } else {
              codeWords = [play.code_word_left || ""];
            }
          }
        } else {
          // For pass plays, just use the regular code word
          codeWords = [play.code_word || ""];
        }

        // Create a play for each code word
        codeWords.forEach((codeWord) => {
          generatedPlays.push({
            ...play,
            formation_direction: direction,
            code_word: codeWord,
          });
        });
      });
    });

    return generatedPlays;
  };
  // defense
  const saveFront = async (front: {
    id?: number;
    name: string;
    description: string;
    club: number;
  }) => {
    setIsLoading(true);
    setError(null);

    try {
      const data = {
        ...front,
        club: front.club || club?.id,
      };
      let response;
      if (front.id) {
        response = await axios.put(`${API_BASE_URL}/fronts/${front.id}/`, data);
      } else {
        response = await axios.post(`${API_BASE_URL}/fronts/`, data);
      }
      setIsLoading(false);
      await fetchFronts();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message || err.message || "Error saving front"
      );
      console.error("Error saving front:", err);
      throw err;
    }
  };

  const deleteFront = async (id: number): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/fronts/${id}/`);
      setIsLoading(false);
      await fetchFronts();
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message || err.message || "Error deleting front"
      );
      console.error("Error deleting front:", err);
      return false;
    }
  };

  const saveStunt = async (stunt: {
    id?: number;
    name: string;
    description: string;
    club: number;
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = {
        ...stunt,
        club: stunt.club || club?.id,
      };
      let response;
      if (stunt.id) {
        response = await axios.put(`${API_BASE_URL}/stunts/${stunt.id}/`, data);
      } else {
        response = await axios.post(`${API_BASE_URL}/stunts/`, data);
      }
      setIsLoading(false);
      await fetchStunts();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message || err.message || "Error saving stunt"
      );
      console.error("Error saving stunt:", err);
      throw err;
    }
  };

  const deleteStunt = async (id: number): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/stunts/${id}/`);
      await fetchStunts();
      setIsLoading(false);
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message || err.message || "Error deleting stunt"
      );
      console.error("Error deleting stunt:", err);
      return false;
    }
  };
  const saveCoverage = async (coverage: {
    id?: number;
    name: string;
    description: string;
    club: number;
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = {
        ...coverage,
        club: coverage.club || club?.id,
      };
      let response;
      if (coverage.id) {
        response = await axios.put(
          `${API_BASE_URL}/coverages/${coverage.id}/`,
          data
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/coverages/`, data);
      }
      setIsLoading(false);
      await fetchCoverages();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message || err.message || "Error saving coverage"
      );
      console.error("Error saving coverage:", err);
      throw err;
    }
  };
  const deleteCoverage = async (id: number): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/coverages/${id}/`);
      await fetchCoverages();
      setIsLoading(false);
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message || err.message || "Error deleting coverage"
      );
      console.error("Error deleting coverage:", err);
      return false;
    }
  };

  // kickoff
  const saveKickOffPlay = async (play: KickOffPlay) => {
    setIsLoading(true);
    setError(null);

    if (!play.personnel_group) {
      setError("Personnel group is required");
      setIsLoading(false);
      return;
    }

    try {
      const data = {
        ...play,
        personnel_group_id: play.personnel_group.id,
        club: play.club_id || club?.id,
      };
      let response;
      if (play.id) {
        response = await axios.put(
          `${API_BASE_URL}/kickoff-plays/${play.id}/`,
          data
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/kickoff-plays/`, data);
      }

      setIsLoading(false);
      await fetchKickOffPlays();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error saving play concept"
      );
      console.error("Error saving play concept:", err);
      throw err;
    }
  };
  const deleteKickOffPlay = async (id: number): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/kickoff-plays/${id}/`);
      await fetchKickOffPlays();
      setIsLoading(false);
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error deleting kickoff play"
      );
      console.error("Error deleting kickoff play:", err);
      return false;
    }
  };
  // kickoff return
  const saveKickOffReturnPlay = async (play: KickOffReturnPlay) => {
    setIsLoading(true);
    setError(null);

    if (!play.personnel_group) {
      setError("Personnel group is required");
      setIsLoading(false);
      return;
    }

    try {
      const data = {
        ...play,
        personnel_group_id: play.personnel_group.id,
        club: play.club_id || club?.id,
      };
      console.log(data);

      let response;
      if (play.id) {
        response = await axios.put(
          `${API_BASE_URL}/kickoff-return-plays/${play.id}/`,
          data
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/kickoff-return-plays/`,
          data
        );
      }

      setIsLoading(false);
      await fetchKickOffReturnPlays();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error saving play concept"
      );
      console.error("Error saving play concept:", err);
      throw err;
    }
  };
  const deleteKickOffReturnPlay = async (id: number): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/kickoff-return-plays/${id}/`);
      await fetchKickOffReturnPlays();
      setIsLoading(false);
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error deleting kickoff return play"
      );
      console.error("Error deleting kickoff return play:", err);
      return false;
    }
  };
  // punt
  const savePuntPlay = async (play: PuntPlay) => {
    setIsLoading(true);
    setError(null);

    if (!play.personnel_group) {
      setError("Personnel group is required");
      setIsLoading(false);
      return;
    }

    try {
      const data = {
        ...play,
        personnel_group_id: play.personnel_group.id,
        club: play.club_id || club?.id,
      };
      console.log(data);

      let response;
      if (play.id) {
        response = await axios.put(
          `${API_BASE_URL}/punt-plays/${play.id}/`,
          data
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/punt-plays/`, data);
      }

      setIsLoading(false);
      await fetchPuntPlays();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message || err.message || "Error saving punt play"
      );
      console.error("Error saving punt play:", err);
      throw err;
    }
  };
  const deletePuntPlay = async (id: number): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/punt-plays/${id}/`);
      await fetchPuntPlays();
      setIsLoading(false);
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message || err.message || "Error deleting punt play"
      );
      console.error("Error deleting punt play:", err);
      return false;
    }
  };
  // punt return
  const savePuntReturnPlay = async (play: PuntReturnPlay) => {
    setIsLoading(true);
    setError(null);

    if (!play.personnel_group) {
      setError("Personnel group is required");
      setIsLoading(false);
      return;
    }

    try {
      const data = {
        ...play,
        personnel_group_id: play.personnel_group.id,
        club: play.club_id || club?.id,
      };
      console.log(data);

      let response;
      if (play.id) {
        response = await axios.put(
          `${API_BASE_URL}/punt-return-plays/${play.id}/`,
          data
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/punt-return-plays/`, data);
      }

      setIsLoading(false);
      await fetchPuntReturnPlays();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error saving punt return play"
      );
      console.error("Error saving punt return play:", err);
      throw err;
    }
  };
  const deletePuntReturnPlay = async (id: number): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/punt-return-plays/${id}/`);
      await fetchPuntReturnPlays();
      setIsLoading(false);
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error deleting punt return play"
      );
      console.error("Error deleting punt return play:", err);
      return false;
    }
  };
  // field goal
  const saveFieldGoalPlay = async (play: FieldGoalPlay) => {
    setIsLoading(true);
    setError(null);

    if (!play.personnel_group) {
      setError("Personnel group is required");
      setIsLoading(false);
      return;
    }

    try {
      const data = {
        ...play,
        personnel_group_id: play.personnel_group.id,
        club: play.club_id || club?.id,
      };
      let response;
      if (play.id) {
        response = await axios.put(
          `${API_BASE_URL}/field-goal-plays/${play.id}/`,
          data
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/field-goal-plays/`, data);
      }

      setIsLoading(false);
      await fetchFieldGoalPlays();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error saving field goal play"
      );
      console.error("Error saving field goal play:", err);
      throw err;
    }
  };

  const deleteFieldGoalPlay = async (id: number): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/field-goal-plays/${id}/`);
      await fetchFieldGoalPlays();
      setIsLoading(false);
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error deleting field goal play"
      );
      console.error("Error deleting field goal play:", err);
      return false;
    }
  };
  // field goal block
  const saveFieldGoalBlockPlay = async (play: FieldGoalBlockPlay) => {
    setIsLoading(true);
    setError(null);

    if (!play.personnel_group) {
      setError("Personnel group is required");
      setIsLoading(false);
      return;
    }

    try {
      const data = {
        ...play,
        personnel_group_id: play.personnel_group.id,
        club: play.club_id || club?.id,
      };
      let response;
      if (play.id) {
        response = await axios.put(
          `${API_BASE_URL}/field-goal-block-plays/${play.id}/`,
          data
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/field-goal-block-plays/`,
          data
        );
      }

      setIsLoading(false);
      await fetchFieldGoalBlockPlays();
      return response.data;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error saving field goal block play"
      );
      console.error("Error saving field goal block play:", err);
      throw err;
    }
  };

  const deleteFieldGoalBlockPlay = async (id: number): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/field-goal-block-plays/${id}/`);
      await fetchFieldGoalBlockPlays();
      setIsLoading(false);
      return true;
    } catch (err: any) {
      setIsLoading(false);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error deleting field goal block play"
      );
      console.error("Error deleting field goal block play:", err);
      return false;
    }
  };

  // Combine all methods and state into the context value
  const contextValue: SchemesContextType = {
    // Data states
    isLoading,
    error,
    // all
    personnel,
    // offense
    formations,
    formationTags,
    offensivePlayTags,
    playConcepts,
    runConcepts,
    passConcepts,
    passProtectionConcepts,
    offensivePersonnel,
    // deprecated (perhaps reinstate in future versions)
    offensivePlaybookPlays,
    offensiveSituations,
    offensiveGamePlans,
    // defense
    defensivePersonnel,
    fronts,
    stunts,
    coverages,
    // kickoff
    kickOffPersonnel,
    kickOffPlays,
    // kickoff return
    kickOffReturnPersonnel,
    kickOffReturnPlays,
    // punt
    puntPersonnel,
    puntPlays,
    // punt return
    puntReturnPersonnel,
    puntReturnPlays,
    // field goal
    fieldGoalPersonnel,
    fieldGoalPlays,
    // field goal block
    fieldGoalBlockPersonnel,
    fieldGoalBlockPlays,
    // Data fetching methods
    // all
    fetchPersonnel,
    // offense
    fetchFormations,
    fetchTags,
    fetchFormationTags,
    fetchPlayTags,
    fetchPlayConcepts,
    fetchRunConcepts,
    fetchPassConcepts,
    fetchPassProtectionConcepts,
    fetchOffensiveSituations,
    fetchOffensiveGamePlans,
    // defense
    fetchFronts,
    fetchStunts,
    fetchCoverages,
    //special teams
    fetchKickOffPlays,
    fetchKickOffReturnPlays,
    fetchPuntPlays,
    fetchPuntReturnPlays,
    fetchFieldGoalPlays,
    fetchFieldGoalBlockPlays,
    // Data mutation methods
    // all
    savePersonnelGroup,
    deletePersonnelGroup,
    // offense
    saveFormation,
    deleteFormation,
    saveFormationTag,
    deleteFormationTag,
    savePlayTag,
    deletePlayTag,
    savePlayConcept,
    deletePlayConcept,
    saveRunConcept,
    deleteRunConcept,
    savePassConcept,
    deletePassConcept,
    savePassProtectionConcept,
    deletePassProtectionConcept,
    // deprecated (perhaps reinstate in future versions)
    generateOffensivePlaybookPlays,
    generateGameplanPlays,
    saveOffensiveGamePlan,
    updateOffensiveGamePlan,
    deleteOffensiveGamePlan,
    // defense
    saveFront,
    deleteFront,
    saveStunt,
    deleteStunt,
    saveCoverage,
    deleteCoverage,
    //kickoff
    saveKickOffPlay,
    deleteKickOffPlay,
    // kickoff return
    saveKickOffReturnPlay,
    deleteKickOffReturnPlay,
    // punt
    savePuntPlay,
    deletePuntPlay,
    // punt return
    savePuntReturnPlay,
    deletePuntReturnPlay,
    // field goal
    saveFieldGoalPlay,
    deleteFieldGoalPlay,
    // field goal block
    saveFieldGoalBlockPlay,
    deleteFieldGoalBlockPlay,
  };

  return (
    <SchemesContext.Provider value={contextValue}>
      {children}
    </SchemesContext.Provider>
  );
};

// Custom hook to use the schemes context
export const useSchemes = () => {
  const context = useContext(SchemesContext);
  if (context === undefined) {
    throw new Error("useSchemes must be used within a SchemesProvider");
  }
  return context;
};
